import React from 'react';
import { Box, Container, Grid, Typography, Paper, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import { EmojiEvents, Handshake, TrendingUp } from '@mui/icons-material';
import { fadeInUp, fadeInLeft, staggerContainer, viewportConfig } from '../../utils/animations';
import { COMPANY_INFO } from '../../utils/constants';
import aboutUs from '../../assets/about_us.jpg';

const milestones = [
  {
    icon: EmojiEvents,
    title: 'Our Journey',
    description: 'Founded by Mark Kieru, a seasoned real estate professional with a passion for delivering exceptional service.',
  },
  {
    icon: Handshake,
    title: 'Our Mission',
    description: 'To provide property owners with peace of mind, knowing their investments are in capable hands.',
  },
  {
    icon: TrendingUp,
    title: 'Our Vision',
    description: 'To be the leading property management agency, known for excellence, integrity, and innovation.',
  },
];

const About = () => {
  return (
    <Box
      id="about"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: { xs: 200, md: 400 },
          height: { xs: 200, md: 400 },
          background: 'linear-gradient(135deg, rgba(201, 169, 97, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          transform: 'translate(30%, -30%)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInLeft}
              sx={{ position: 'relative' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(30, 58, 95, 0.15)',
                }}
              >
                <Box
                  component="img"
                  src={aboutUs}
                  alt="About ITorama Property Management"
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 450 },
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>

              <Paper
                component={motion.div}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.5 }}
                elevation={4}
                sx={{
                  position: 'absolute',
                  bottom: { xs: -20, md: -30 },
                  right: { xs: 16, md: -30 },
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  textAlign: 'center',
                  minWidth: { xs: 120, md: 150 },
                }}
              >
                <Typography variant="h3" fontWeight={700} sx={{ color: 'secondary.main' }}>
                  10+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Years of Excellence
                </Typography>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={staggerContainer}
            >
              <Typography
                component={motion.span}
                variants={fadeInUp}
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                About Us
              </Typography>

              <Typography
                component={motion.h2}
                variants={fadeInUp}
                variant="h2"
                sx={{
                  color: 'text.primary',
                  mb: 3,
                }}
              >
                A Legacy of Excellence in Property Management
              </Typography>

              <Typography
                component={motion.p}
                variants={fadeInUp}
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.8,
                }}
              >
                {COMPANY_INFO.name} has been a trusted name in property management since its inception.
                Founded by {COMPANY_INFO.founder}, a seasoned real estate professional with a passion
                for delivering exceptional service, our agency embarked on a mission to redefine the
                property management experience.
              </Typography>

              <Box
                component={motion.div}
                variants={fadeInUp}
                sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
              >
                {milestones.map((milestone, index) => (
                  <Box
                    key={milestone.title}
                    component={motion.div}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Avatar
                      sx={{
                        backgroundColor: 'rgba(201, 169, 97, 0.1)',
                        color: 'secondary.main',
                        width: 48,
                        height: 48,
                      }}
                    >
                      <milestone.icon />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ color: 'text.primary', mb: 0.5, fontWeight: 600 }}
                      >
                        {milestone.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                        {milestone.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
